/**
 * Structural validation of served documents against `spec/surface.schema.json`.
 *
 * A snapshot, a change detail, or a recorded frame reaches templates field by
 * field. Checking only that top-level keys exist let a renamed nested field
 * render as `undefined`, a string-typed count render as `NaN`, and — worse — an
 * object with an `html` property in a string position render as raw markup,
 * because Hydrogen treats such an object as a component. Every row is checked
 * against the one declaration before any of it is trusted.
 *
 * The validator covers the keyword subset the schema uses: `$ref`, `type`,
 * `const`, `enum`, `oneOf`, `anyOf`, `properties`/`required`, `items`, `minimum`, and
 * the integer `format`s. An unknown keyword fails closed rather than passing.
 */

import { SURFACE_SCHEMA } from '../generated/surface-schema.js';
import type { ChangeDetail, StateDelta, StateFiles, SurfaceSnapshot } from '../generated/surface.js';

export type JsonSchema = {
  $ref?: string;
  type?: string | string[];
  const?: unknown;
  enum?: unknown[];
  oneOf?: JsonSchema[];
  anyOf?: JsonSchema[];
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  minimum?: number;
  format?: string;
  $defs?: Record<string, JsonSchema>;
};

const KNOWN = new Set(['$ref', 'type', 'const', 'enum', 'oneOf', 'anyOf', 'properties', 'required', 'items', 'minimum', 'format', '$defs']);
const INTEGER_FORMATS = new Set(['uint64', 'uint32', 'uint', 'int64', 'int32']);

function typeMatches(type: string, value: unknown): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string';
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'null':
      return value === null;
    case 'array':
      return Array.isArray(value);
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    default:
      return false;
  }
}

/** The first place `value` departs from `node`, as `path: reason`, or null. */
export function schemaViolation(value: unknown, node: JsonSchema, root: JsonSchema = SURFACE_SCHEMA, path = '$'): string | null {
  for (const key of Object.keys(node)) if (!KNOWN.has(key)) return `${path}: schema keyword ${key} is not understood`;
  if (node.$ref !== undefined) {
    const name = /^#\/\$defs\/(\w+)$/.exec(node.$ref)?.[1];
    const target = name ? root.$defs?.[name] : undefined;
    if (!target) return `${path}: unresolvable ${node.$ref}`;
    const found = schemaViolation(value, target, root, path);
    if (found) return found;
  }
  if (node.type !== undefined) {
    const types = Array.isArray(node.type) ? node.type : [node.type];
    if (!types.some((type) => typeMatches(type, value))) return `${path}: expected ${types.join(' | ')}`;
  }
  if ('const' in node && value !== node.const) return `${path}: expected ${JSON.stringify(node.const)}`;
  if (node.enum !== undefined && !node.enum.includes(value)) return `${path}: not one of ${node.enum.map((item) => JSON.stringify(item)).join(', ')}`;
  if (typeof value === 'number') {
    if (node.minimum !== undefined && value < node.minimum) return `${path}: below ${node.minimum}`;
    if (node.format !== undefined && INTEGER_FORMATS.has(node.format) && !Number.isSafeInteger(value)) return `${path}: not a ${node.format}`;
  }
  if (node.oneOf !== undefined) {
    const matches = node.oneOf.filter((option) => schemaViolation(value, option, root, path) === null).length;
    if (matches !== 1) return `${path}: matches ${matches} of ${node.oneOf.length} alternatives`;
  }
  if (node.anyOf !== undefined && !node.anyOf.some((option) => schemaViolation(value, option, root, path) === null))
    return `${path}: matches none of ${node.anyOf.length} alternatives`;
  if (typeMatches('object', value)) {
    const record = value as Record<string, unknown>;
    for (const key of node.required ?? []) if (!(key in record)) return `${path}.${key}: missing`;
    for (const [key, child] of Object.entries(node.properties ?? {})) {
      if (!(key in record)) continue;
      const found = schemaViolation(record[key], child, root, `${path}.${key}`);
      if (found) return found;
    }
  }
  if (Array.isArray(value) && node.items !== undefined) {
    for (const [index, item] of value.entries()) {
      const found = schemaViolation(item, node.items, root, `${path}[${index}]`);
      if (found) return found;
    }
  }
  return null;
}

/** The schema's named definition, for rows served outside a snapshot. */
export function surfaceDefinition(name: string): JsonSchema {
  const definition = SURFACE_SCHEMA.$defs?.[name];
  if (!definition) throw new Error(`spec/surface.schema.json declares no ${name}`);
  return definition;
}

/*
 * Documents served beside the snapshot. The spec schema does not declare them,
 * so their envelopes are declared here, mirroring the Worker's serializers;
 * every row inside them is checked against the spec's own definitions.
 */
const STRING: JsonSchema = { type: 'string' };
const STRINGS: JsonSchema = { type: 'array', items: STRING };
const COUNT: JsonSchema = { type: 'integer', format: 'uint64', minimum: 0 };
const NULLABLE_STRING: JsonSchema = { type: ['string', 'null'] };

const STATE_DELTA: JsonSchema = {
  type: 'object',
  properties: { base_state: STRING, result_state: STRING, added: STRINGS, removed: STRINGS, modified: STRINGS },
  required: ['base_state', 'result_state', 'added', 'removed', 'modified'],
};

const STATE_FILES: JsonSchema = {
  type: 'object',
  properties: {
    root: STRING,
    files: {
      type: 'array',
      items: { type: 'object', properties: { path: STRING, content_digest: STRING }, required: ['path', 'content_digest'] },
    },
  },
  required: ['root', 'files'],
};

const CHANGE_DETAIL: JsonSchema = {
  type: 'object',
  properties: {
    change: { $ref: '#/$defs/ChangeRow' },
    mutations: {
      type: 'array',
      items: {
        type: 'object',
        properties: { ordinal: COUNT, kind: STRING, path: STRING, content_digest: NULLABLE_STRING },
        required: ['ordinal', 'kind', 'path', 'content_digest'],
      },
    },
    evidence: { type: 'array', items: { $ref: '#/$defs/EvidenceRow' } },
    fabric_run: { anyOf: [{ type: 'null' }, { $ref: '#/$defs/FabricRunRow' }] },
    certificate: { anyOf: [{ type: 'null' }, { $ref: '#/$defs/CertificateRow' }] },
    delta: { anyOf: [{ type: 'null' }, STATE_DELTA] },
  },
  required: ['change', 'mutations', 'evidence', 'fabric_run', 'certificate', 'delta'],
};

export function isChangeDetail(value: unknown): value is ChangeDetail {
  return schemaViolation(value, CHANGE_DETAIL) === null;
}

export function isStateFiles(value: unknown): value is StateFiles {
  return schemaViolation(value, STATE_FILES) === null;
}

export function isStateDelta(value: unknown): value is StateDelta {
  return schemaViolation(value, STATE_DELTA) === null;
}

export function snapshotViolation(value: unknown): string | null {
  return schemaViolation(value, SURFACE_SCHEMA);
}

export function isSurfaceSnapshot(value: unknown): value is SurfaceSnapshot {
  return snapshotViolation(value) === null;
}
